import React, { useEffect, useRef } from "react";
import { Check, Star } from "lucide-react";
import { track } from "../lib/analytics";

/**
 * PricingTiers — Three infrastructure tiers. Middle card ("Rose Gold ·
 * Command") is highlighted as the most-popular tier. Every CTA opens
 * Calendly in a new tab per the Director's directive.
 */
const CALENDLY_URL = "https://calendly.com/empowerinvestgrow/30min";
const GOLD = "#C9A84C";
const ROSE = "#D8A48F";

const TIERS = [
    {
        name: "Platinum",
        subtitle: "Foundation",
        setup: "$3,500",
        monthly: "$497/mo",
        description:
            "Essential digital infrastructure — website, CRM, and core automations to launch with confidence.",
        highlights: [
            "Sovereign website build",
            "CRM + contact intake",
            "Core automations & email flows",
        ],
        featured: false,
        testid: "tier-platinum",
        accent: GOLD,
    },
    {
        name: "Rose Gold",
        subtitle: "Command",
        setup: "$7,500",
        monthly: "$897/mo",
        description:
            "Advanced operations suite — multi-channel marketing, payment processing, and business intelligence dashboards.",
        highlights: [
            "Everything in Platinum",
            "Multi-channel marketing stack",
            "Payments + BI dashboards",
        ],
        featured: true,
        testid: "tier-rose-gold",
        accent: ROSE,
    },
    {
        name: "Diamond",
        subtitle: "Sovereign",
        setup: "$15,000+",
        monthly: "$1,497+/mo",
        description:
            "Full sovereign infrastructure — custom integrations, dedicated support, AI-powered workflows, and enterprise-grade security.",
        highlights: [
            "Everything in Rose Gold",
            "Custom integrations & AI workflows",
            "Dedicated support · enterprise security",
        ],
        featured: false,
        testid: "tier-diamond",
        accent: GOLD,
    },
];

function TierCard({ tier }) {
    const outerStyle = tier.featured
        ? {
              background: "#0F0D08",
              border: `1px solid ${GOLD}`,
              boxShadow: `0 0 0 1px rgba(201,168,76,0.25), 0 20px 60px rgba(201,168,76,0.12)`,
              transform: "translateY(-6px)",
          }
        : {
              background: "#0A0A0A",
              border: "1px solid rgba(201,168,76,0.12)",
              borderLeft: `3px solid ${GOLD}`,
          };

    return (
        <div
            data-testid={tier.testid}
            className="relative p-8 rounded-[10px] transition-all duration-300 flex flex-col"
            style={outerStyle}
            onMouseEnter={(e) => {
                if (!tier.featured) {
                    e.currentTarget.style.borderColor = "rgba(201,168,76,0.4)";
                    e.currentTarget.style.borderLeft = `3px solid ${GOLD}`;
                }
                e.currentTarget.style.transform = tier.featured
                    ? "translateY(-8px)"
                    : "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
                if (!tier.featured) {
                    e.currentTarget.style.borderColor = "rgba(201,168,76,0.12)";
                    e.currentTarget.style.borderLeft = `3px solid ${GOLD}`;
                }
                e.currentTarget.style.transform = tier.featured
                    ? "translateY(-6px)"
                    : "translateY(0)";
            }}
        >
            {tier.featured && (
                <div
                    data-testid="tier-badge-most-popular"
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.3em] flex items-center gap-1.5"
                    style={{
                        background: GOLD,
                        color: "#0A0A0A",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        letterSpacing: "0.22em",
                    }}
                >
                    <Star size={10} fill="#0A0A0A" strokeWidth={0} /> Most Popular
                </div>
            )}

            <div
                className="text-[10px] uppercase tracking-[0.35em] mb-1"
                style={{
                    color: tier.accent,
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                }}
            >
                {tier.name}
            </div>
            <div
                className="text-2xl mb-6"
                style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                    color: "#EDEDED",
                    letterSpacing: "-0.02em",
                }}
            >
                {tier.subtitle}
            </div>

            <div className="mb-6">
                <div className="flex items-baseline gap-2">
                    <span
                        className="text-4xl"
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            color: "#EDEDED",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {tier.setup}
                    </span>
                    <span className="text-xs text-muted-foreground">setup</span>
                </div>
                <div className="mt-1 text-sm" style={{ color: tier.accent }}>
                    + {tier.monthly}
                </div>
            </div>

            <p
                className="text-sm leading-relaxed mb-6"
                style={{
                    fontFamily: "'Inter', 'Montserrat', sans-serif",
                    color: "#A8A8A8",
                }}
            >
                {tier.description}
            </p>

            <ul className="space-y-3 mb-8 flex-1">
                {tier.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-sm" style={{ color: "#C8C8C8" }}>
                        <Check size={16} style={{ color: tier.accent, flexShrink: 0, marginTop: 2 }} />
                        <span>{h}</span>
                    </li>
                ))}
            </ul>

            <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${tier.testid}-cta`}
                className="group inline-flex items-center justify-center gap-2 w-full px-6 py-3 font-bold uppercase tracking-[0.2em] text-xs transition-all duration-300"
                style={
                    tier.featured
                        ? {
                              background: GOLD,
                              color: "#0A0A0A",
                              border: `1px solid ${GOLD}`,
                              fontFamily: "'Montserrat', sans-serif",
                              boxShadow: "0 0 22px rgba(201,168,76,0.35)",
                          }
                        : {
                              background: "transparent",
                              color: GOLD,
                              border: `1px solid ${GOLD}`,
                              fontFamily: "'Montserrat', sans-serif",
                          }
                }
                onMouseEnter={(e) => {
                    if (tier.featured) {
                        e.currentTarget.style.boxShadow = "0 0 32px rgba(201,168,76,0.6)";
                    } else {
                        e.currentTarget.style.background = "rgba(201,168,76,0.08)";
                    }
                }}
                onMouseLeave={(e) => {
                    if (tier.featured) {
                        e.currentTarget.style.boxShadow = "0 0 22px rgba(201,168,76,0.35)";
                    } else {
                        e.currentTarget.style.background = "transparent";
                    }
                }}
            >
                Get Started
            </a>
        </div>
    );
}

export default function PricingTiers() {
    const sectionRef = useRef(null);

    useEffect(() => {
        // Fire biaas_pricing_view once when the pricing section becomes
        // >= 25% visible — this is the top of the funnel we compare
        // against successful form submits.
        const node = sectionRef.current;
        if (!node || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        track("biaas_pricing_view", { path: "/" }, { once: true });
                        io.disconnect();
                    }
                });
            },
            { threshold: 0.25 }
        );
        io.observe(node);
        return () => io.disconnect();
    }, []);

    return (
        <section
            id="pricing-tiers"
            data-testid="pricing-tiers-section"
            ref={sectionRef}
            className="relative py-24 md:py-32"
            style={{ background: "#080808" }}
        >
            <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                <div className="max-w-2xl mb-16">
                    <div
                        className="text-[10px] uppercase tracking-[0.4em] mb-4"
                        style={{ color: GOLD, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}
                        data-testid="pricing-overline"
                    >
                        Pricing
                    </div>
                    <h2
                        className="text-4xl md:text-5xl leading-[1.1] tracking-tight"
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 700,
                            color: "#EDEDED",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Choose Your Infrastructure Tier
                    </h2>
                    <p
                        className="mt-4 text-base md:text-lg"
                        style={{ color: "#A8A8A8", fontFamily: "'Inter', 'Montserrat', sans-serif" }}
                    >
                        Every tier includes white-glove onboarding and dedicated support.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
                    {TIERS.map((t) => (
                        <TierCard key={t.name} tier={t} />
                    ))}
                </div>
            </div>
        </section>
    );
}
